import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMatch, useJob, useDeleteJobPosting } from '../../hooks/useJobs';
import { useAuth } from '../../hooks/useAuth';
import { formatJobTitle, getJobStatusLabel, getJobStatusColor } from '../../types/job';
import AcceptRejectButtons from './AcceptRejectButtons';
import './JobDetailsModal.css';

const JobDetailsModal = ({ isOpen, onClose, event, job, events, view = 'detail', onEventSelect, onBackToList, onRemoveAvailability }) => {
  const { user } = useAuth();
  const userRole = user?.role_name;
  const navigate = useNavigate();
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const deleteJobPostingMutation = useDeleteJobPosting();
  const contentRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);
  
  // React Hooks must be called unconditionally, before any early returns
  // If this is a potential match, fetch match details
  const matchId = event?.extendedProps?.match_id;
  const { data: match, isLoading: isLoadingMatch } = useMatch(matchId, {
    enabled: !!matchId && isOpen && !!event && view === 'detail'
  });

  // Fetch job details if job ID is provided
  // For match events, the job might be in match.job_posting_detail, so we only fetch if not available
  const jobId = job?.id || event?.extendedProps?.job_id;
  const shouldFetchJob = !!jobId && isOpen && !!event && view === 'detail' && !job && !match?.job_posting_detail;
  const { data: fetchedJob, isLoading: isLoadingJob } = useJob(jobId, {
    enabled: shouldFetchJob
  });

  useEffect(() => {
    if (!isOpen) {
      setHasScroll(false);
      return;
    }

    const el = contentRef.current;
    if (!el) return;

    // Measure after layout to determine if content overflows
    const timeoutId = setTimeout(() => {
      if (!el) return;
      setHasScroll(el.scrollHeight > el.clientHeight + 8);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  // Early return after hooks
  if (!isOpen) return null;
  
  // If list view and no events, return null
  if (view === 'list' && (!events || events.length === 0)) return null;
  
  // If detail view and no event, return null
  if (view === 'detail' && !event) return null;

  const eventType = view === 'detail' && event ? event.extendedProps?.type : null;
  
  // Get match data (only for detail view)
  const displayMatch = view === 'detail' && event ? (match || event.extendedProps?.match) : null;
  
  // Get job data - handle nested structures from API (only for detail view)
  let displayJob = view === 'detail' && event ? (job || fetchedJob || event.extendedProps?.job) : null;
  
  // If we have a match, get job from match.job_posting_detail and merge match-level fields
  if (!displayJob && displayMatch) {
    const jobFromMatch = displayMatch.job_posting_detail || displayMatch.job_posting || displayMatch.job;
    if (jobFromMatch) {
      // Merge job_posting_detail with match-level fields
      // IMPORTANT: start_time and end_time should come from job_posting_detail, not match level
      displayJob = {
        ...jobFromMatch,
        // Use job_posting_detail time fields (they are in the job posting, not at match level)
        start_time: jobFromMatch.start_time || displayMatch.start_time,
        end_time: jobFromMatch.end_time || displayMatch.end_time,
        // number_of_hours might be at match level
        number_of_hours: displayMatch.number_of_hours !== undefined ? displayMatch.number_of_hours : jobFromMatch.number_of_hours,
        // Preserve date from job_posting_detail
        date: jobFromMatch.date || displayMatch.date,
      };
    } else if (displayMatch) {
      // If no job_posting_detail, try to construct from match-level data
      displayJob = {
        number_of_hours: displayMatch.number_of_hours,
        start_time: displayMatch.start_time,
        end_time: displayMatch.end_time,
        date: displayMatch.date,
      };
    }
  }
  
  // Normalize job data structure to handle API response format
  if (displayJob) {
    // Normalize job role - check nested job_role_detail first
    // job_role can be a number (ID) or a string, so check if it's a number or missing
    if (typeof displayJob.job_role === 'number' || !displayJob.job_role || displayJob.job_role === 'N/A') {
      displayJob.job_role = displayJob.job_role_detail?.job_role_name 
        || displayJob.job_role_detail?.job_role 
        || displayJob.job_role_name
        || displayMatch?.job_posting_detail?.job_role_detail?.job_role_name
        || 'N/A';
    }
    
    // Normalize employer name - also check match.job_posting_detail
    if (!displayJob.employer_name || displayJob.employer_name === 'N/A') {
      displayJob.employer_name = displayMatch?.job_posting_detail?.employer_name
        || (displayJob.employer?.first_name && displayJob.employer?.last_name 
          ? `${displayJob.employer.first_name} ${displayJob.employer.last_name}`
          : displayJob.employer?.practice_name
          || displayJob.employer?.email
          || 'N/A');
    }
    
    // Normalize date field - ensure job_date is set from date field
    if (!displayJob.job_date) {
      displayJob.job_date = displayJob.date 
        || displayMatch?.job_posting_detail?.date
        || null;
    }
    
    // Normalize hours field - prioritize match level
    if (!displayJob.total_hours) {
      displayJob.total_hours = displayJob.number_of_hours || displayMatch?.number_of_hours;
    }
    if (!displayJob.number_of_hours && displayMatch?.number_of_hours) {
      displayJob.number_of_hours = displayMatch.number_of_hours;
    }
    
    // Normalize time fields - these should already be set, but ensure they're available
    if (!displayJob.start_time) {
      displayJob.start_time = displayMatch?.job_posting_detail?.start_time
        || displayMatch?.start_time
        || null;
    }
    if (!displayJob.end_time) {
      displayJob.end_time = displayMatch?.job_posting_detail?.end_time
        || displayMatch?.end_time
        || null;
    }
    
    // Normalize address field - also check match.job_posting_detail
    if (!displayJob.practice_address && !displayJob.employer_address) {
      displayJob.employer_address = displayMatch?.job_posting_detail?.employer_address
        || displayJob.employer_address
        || displayJob.employer?.address
        || null;
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    // Handle both "HH:MM" and "HH:MM:SS" formats
    const timeParts = timeStr.split(':');
    const hours = timeParts[0];
    const minutes = timeParts[1] || '00';
    const hour = parseInt(hours, 10);
    if (isNaN(hour)) return 'N/A';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getEventTypeLabel = () => {
    if (!eventType) return 'Event';
    switch (eventType) {
      case 'availability':
        return 'Availability';
      case 'posted_job':
        return 'Posted Job';
      case 'potential_match':
        return 'Potential Match';
      case 'accepted_job':
        return 'Accepted Job';
      case 'completed_job':
        return 'Completed Job';
      default:
        return 'Event';
    }
  };

  const handleActionComplete = () => {
    // Refresh calendar events after action
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  // Check if job can be deleted (only open jobs can be deleted)
  const canDeleteJob = (jobData) => {
    if (userRole !== 'employer') return false;
    if (!jobData) return false;
    // Only allow deletion of jobs that are open or pending (not accepted/completed)
    const status = jobData.status || displayJob?.status;
    return status === 'open' || status === 'pending' || status === 'match_found';
  };

  const handleDeleteClick = () => {
    const jobId = displayJob?.id || job?.id || event?.extendedProps?.job_id;
    if (jobId) {
      setDeleteConfirmId(jobId);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      try {
        await deleteJobPostingMutation.mutateAsync(deleteConfirmId);
        setDeleteConfirmId(null);
        // Close modal after successful deletion
        setTimeout(() => {
          onClose();
        }, 500);
      } catch (error) {
        // Error is handled by the mutation's onError
        console.error('Delete error:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  // Generate title based on user role using job type helper
  const getModalTitle = () => {
    if (view === 'list') {
      const dateStr = events && events.length > 0 ? events[0].start : '';
      return `Events on ${formatDate(dateStr)}`;
    }
    if (!displayJob) return getEventTypeLabel();
    return formatJobTitle(displayJob, userRole, displayMatch);
  };

  // Get event type label for an event
  const getEventTypeLabelForEvent = (evt) => {
    const evtType = evt?.extendedProps?.type;
    switch (evtType) {
      case 'availability':
        return 'Availability';
      case 'posted_job':
        return 'Posted Job';
      case 'potential_match':
        return 'Potential Match';
      case 'accepted_job':
        return 'Accepted Job';
      case 'completed_job':
        return 'Completed Job';
      default:
        return 'Event';
    }
  };

  // Get event color based on type
  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'posted_job':
        return '#ff6347';
      case 'potential_match':
        return '#ffa500';
      case 'accepted_job':
        return '#4169e1';
      case 'completed_job':
        return '#9370db';
      case 'availability':
        return '#20b2aa';
      default:
        return '#6b7280';
    }
  };

  return (
    <div className="job-details-modal-overlay" onClick={onClose}>
      <div className="job-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-details-modal__header">
          <div className="job-details-modal__header-content">
            <h2 className="job-details-modal__title">{getModalTitle()}</h2>
            {view === 'detail' && displayJob && (
              <p className="job-details-modal__subtitle">{getEventTypeLabel()}</p>
            )}
            {view === 'detail' && onBackToList && eventType !== 'availability' && (
              <button
                className="job-details-modal__back-button"
                onClick={onBackToList}
              >
                ← Back to list
              </button>
            )}
          </div>
          <button 
            className="job-details-modal__close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="job-details-modal__content" ref={contentRef}>
          {hasScroll && (
            <div className="job-details-modal__scroll-hint">
              <span>Scroll to see all details</span>
              <span className="job-details-modal__scroll-hint-arrow">↓</span>
            </div>
          )}
          {view === 'list' && events && events.length > 0 && (
            <div className="job-details-modal__section">
              <h3 className="job-details-modal__section-title">All Events</h3>
              <div className="job-details-modal__events-list">
                {events.map((evt) => {
                  const eventType = evt.extendedProps?.type;
                  const eventColor = getEventColor(eventType);
                  return (
                    <div
                      key={evt.id}
                      className="job-details-modal__event-item"
                      onClick={() => onEventSelect && onEventSelect(evt)}
                      style={{
                        borderColor: eventColor
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '4px',
                            backgroundColor: eventColor,
                            flexShrink: 0
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.25rem' }}>
                            {evt.title}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {getEventTypeLabelForEvent(evt)}
                          </div>
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          →
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'detail' && (
            <>
          {eventType === 'availability' && (
            <div className="job-details-modal__section">
              <p className="job-details-modal__info">
                You have marked this date as available for work.
              </p>
              <div className="job-details-modal__detail">
                <span className="job-details-modal__label">Date:</span>
                <span className="job-details-modal__value">{formatDate(event.start)}</span>
              </div>
            </div>
          )}

          {displayJob && (
            <>
              <div className="job-details-modal__section">
                <h3 className="job-details-modal__section-title">Job Details</h3>
                
                <div className="job-details-modal__detail">
                  <span className="job-details-modal__label">Date:</span>
                  <span className="job-details-modal__value">
                    {formatDate(displayJob.job_date || displayJob.date || displayMatch?.job_posting_detail?.date)}
                  </span>
                </div>

                <div className="job-details-modal__detail">
                  <span className="job-details-modal__label">Time:</span>
                  <span className="job-details-modal__value">
                    {(() => {
                      // Prioritize job_posting_detail time fields
                      const startTime = displayJob.start_time 
                        || displayMatch?.job_posting_detail?.start_time
                        || displayMatch?.start_time;
                      const endTime = displayJob.end_time 
                        || displayMatch?.job_posting_detail?.end_time
                        || displayMatch?.end_time;
                      if (startTime && endTime) {
                        return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                      }
                      return 'N/A';
                    })()}
                  </span>
                </div>

                {/* Display break time - static values for now since backend doesn't support it yet */}
                {(() => {
                  const breakStartTime = displayJob.break_start_time 
                    || displayMatch?.job_posting_detail?.break_start_time;
                  const breakEndTime = displayJob.break_end_time 
                    || displayMatch?.job_posting_detail?.break_end_time;
                  
                  // Show static break time if not available from backend
                  if (!breakStartTime && !breakEndTime) {
                    // Display static break time (12:00 PM - 1:00 PM) as placeholder
                    return (
                      <div className="job-details-modal__detail">
                        <span className="job-details-modal__label">Break Time:</span>
                        <span className="job-details-modal__value">
                          12:00 PM - 1:00 PM <small style={{ color: '#6b7280', marginLeft: '0.5rem' }}>(Estimated)</small>
                        </span>
                      </div>
                    );
                  } else if (breakStartTime && breakEndTime) {
                    return (
                      <div className="job-details-modal__detail">
                        <span className="job-details-modal__label">Break Time:</span>
                        <span className="job-details-modal__value">
                          {formatTime(breakStartTime)} - {formatTime(breakEndTime)}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="job-details-modal__detail">
                  <span className="job-details-modal__label">Job Role:</span>
                  <span className="job-details-modal__value">
                    {displayJob.job_role 
                      || displayJob.job_role_detail?.job_role_name 
                      || displayJob.job_role_detail?.job_role
                      || displayJob.job_role_name
                      || displayMatch?.job_posting_detail?.job_role_detail?.job_role_name
                      || 'N/A'}
                  </span>
                </div>

                {(displayJob.practice_address || displayJob.employer_address || displayJob.employer?.address) && (
                  <div className="job-details-modal__detail">
                    <span className="job-details-modal__label">Practice Address:</span>
                    <span className="job-details-modal__value">
                      {displayJob.practice_address || displayJob.employer_address || displayJob.employer?.address || 'N/A'}
                    </span>
                  </div>
                )}

                {(displayJob.employer_name || displayJob.employer) && (
                  <div className="job-details-modal__detail">
                    <span className="job-details-modal__label">
                      Employer Name:
                    </span>
                    <span className="job-details-modal__value">
                      {displayJob.employer_name 
                        || (displayJob.employer?.first_name && displayJob.employer?.last_name 
                          ? `${displayJob.employer.first_name} ${displayJob.employer.last_name}`
                          : displayJob.employer?.practice_name 
                          || displayJob.employer?.email
                          || 'N/A')}
                    </span>
                  </div>
                )}
              </div>

              {displayMatch && (
                <div className="job-details-modal__section">
                  <h3 className="job-details-modal__section-title">Match Information</h3>
                  
                  {userRole === 'employee' && displayMatch.distance_miles != null && typeof displayMatch.distance_miles !== 'undefined' && (
                    <div className="job-details-modal__detail">
                      <span className="job-details-modal__label">Distance:</span>
                      <span className="job-details-modal__value">
                        {(() => {
                          const distance = typeof displayMatch.distance_miles === 'number' 
                            ? displayMatch.distance_miles 
                            : parseFloat(displayMatch.distance_miles);
                          return !isNaN(distance) ? distance.toFixed(1) : 'N/A';
                        })()} miles
                      </span>
                    </div>
                  )}

                  <div className="job-details-modal__detail">
                    <span className="job-details-modal__label">Match Status:</span>
                    <span className="job-details-modal__value">
                      {displayMatch.status === 'accepted'
                        ? 'Confirmed (Employee Accepted)'
                        : displayMatch.status === 'rejected' || displayMatch.status === 'discarded'
                        ? 'Not Available'
                        : 'Pending Employee Response'}
                    </span>
                  </div>
                </div>
              )}

              {/* Only show accept/reject buttons for employees viewing potential matches */}
              {userRole === 'employee' && (eventType === 'potential_match' || eventType === 'accepted_job') && displayMatch && eventType !== 'completed_job' && (
                <div className="job-details-modal__section">
                  <AcceptRejectButtons 
                    match={displayMatch} 
                    job={displayJob}
                    onActionComplete={handleActionComplete}
                  />
                </div>
              )}
              
              {/* For employers, show match status info */}
              {userRole === 'employer' && displayMatch && eventType !== 'completed_job' && (
                <div className="job-details-modal__section">
                  <AcceptRejectButtons 
                    match={displayMatch} 
                    job={displayJob}
                    onActionComplete={handleActionComplete}
                  />
                </div>
              )}
              
              {eventType === 'completed_job' && (
                <div className="job-details-modal__section">
                  <div className="job-details-modal__info" style={{ backgroundColor: '#e6d5f7', color: '#6b2c91' }}>
                    <strong>✓ Job Completed</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>This job has been completed successfully.</p>
                  </div>

                  {userRole === 'employer' && (
                    <div className="job-details-modal__payment-section">
                      <p className="job-details-modal__info" style={{ marginTop: '1rem', backgroundColor: '#eef2ff', color: '#1e3a8a' }}>
                        You can view and manage payment for this job from your Profile {'>'} Transactions page.
                      </p>
                      <button
                        type="button"
                        className="job-details-modal__button"
                        style={{ marginTop: '0.75rem' }}
                        onClick={() => {
                          navigate('/profile', { state: { openTab: 'transactions' } });
                          if (onClose) {
                            onClose();
                          }
                        }}
                      >
                        View Payment / Transactions
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Delete option for employers viewing their posted jobs */}
              {userRole === 'employer' && eventType === 'posted_job' && canDeleteJob(displayJob) && (
                <div className="job-details-modal__section">
                  <div className="job-details-modal__info" style={{ backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                    <p style={{ margin: 0 }}>
                      You can delete this job posting if it hasn't been accepted yet.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {!displayJob && eventType !== 'availability' && (
            <div className="job-details-modal__section">
              <p className="job-details-modal__info">No additional details available.</p>
            </div>
          )}
            </>
          )}
        </div>

        <div className="job-details-modal__footer">
          {view === 'detail' && eventType === 'availability' && onRemoveAvailability && (
            <button
              className="job-details-modal__button job-details-modal__button--danger"
              onClick={async () => {
                if (event && event.start) {
                  try {
                    await onRemoveAvailability(event.start);
                    onClose();
                  } catch (error) {
                    console.error('Error removing availability:', error);
                  }
                }
              }}
            >
              Remove Availability
            </button>
          )}
          {view === 'detail' && userRole === 'employer' && eventType === 'posted_job' && canDeleteJob(displayJob) && (
            <button
              className="job-details-modal__button job-details-modal__button--danger"
              onClick={handleDeleteClick}
              disabled={deleteJobPostingMutation.isPending}
            >
              {deleteJobPostingMutation.isPending ? 'Deleting...' : 'Delete Job'}
            </button>
          )}
          {view === 'detail' && onBackToList && eventType !== 'availability' && (
            <button
              className="job-details-modal__button job-details-modal__button--secondary"
              onClick={onBackToList}
            >
              Back to List
            </button>
          )}
          <button
            className="job-details-modal__button"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="job-details-modal__delete-overlay" onClick={handleDeleteCancel}>
            <div className="job-details-modal__delete-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="job-details-modal__delete-title">Delete Job Posting</h3>
              <p className="job-details-modal__delete-message">
                Are you sure you want to delete this job posting? This action cannot be undone.
              </p>
              <div className="job-details-modal__delete-actions">
                <button
                  className="job-details-modal__delete-button job-details-modal__delete-button--cancel"
                  onClick={handleDeleteCancel}
                  disabled={deleteJobPostingMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  className="job-details-modal__delete-button job-details-modal__delete-button--confirm"
                  onClick={handleDeleteConfirm}
                  disabled={deleteJobPostingMutation.isPending}
                >
                  {deleteJobPostingMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailsModal;

